import { useState, useRef, useEffect } from 'react';
import { ResponsiveContainer } from 'recharts';

/**
 * SafeResponsiveContainer - A wrapper around Recharts' ResponsiveContainer
 * that prevents the "width(-1) and height(-1)" warnings by:
 * 1. Ensuring minimum dimensions are always set
 * 2. Only rendering the chart when the container has positive dimensions
 * 3. Using ResizeObserver to detect visibility changes
 * 
 * This solves the common issue of charts rendering in hidden tabs with 0 dimensions.
 * 
 * Usage: Replace <ResponsiveContainer> with <SafeResponsiveContainer>
 */
export default function SafeResponsiveContainer({ 
  children, 
  width = "100%", 
  height = "100%",
  minWidth = 0,
  minHeight = 200,
  aspect,
  debounce = 0,
  ...props 
}) {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Check initial visibility
    const checkVisibility = () => {
      const rect = container.getBoundingClientRect();
      const hasSize = rect.width > 0 && rect.height > 0;
      setContainerSize({ width: rect.width, height: rect.height });
      setIsVisible(hasSize);
    };

    // Initial check
    checkVisibility();

    // Use ResizeObserver for dynamic visibility changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const hasSize = width > 0 && height > 0;
        setContainerSize({ width, height });
        setIsVisible(hasSize);
      }
    });

    resizeObserver.observe(container);

    // Also check on visibility change (for tab switching)
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            checkVisibility();
          }
        });
      },
      { threshold: 0.1 }
    );

    intersectionObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: typeof width === 'number' ? width : width, 
        height: typeof height === 'number' ? height : height,
        minWidth: minWidth,
        minHeight: minHeight 
      }}
    >
      {isVisible && containerSize.width > 0 && containerSize.height > 0 ? (
        <ResponsiveContainer 
          width="100%" 
          height="100%"
          minWidth={minWidth}
          minHeight={minHeight}
          aspect={aspect}
          debounce={debounce}
          {...props}
        >
          {children}
        </ResponsiveContainer>
      ) : (
        <div 
          className="flex items-center justify-center text-zinc-600 text-sm"
          style={{ width: '100%', height: '100%', minHeight: minHeight }}
        >
          {/* Empty placeholder while waiting for visibility */}
        </div>
      )}
    </div>
  );
}
